import { __assign, __awaiter, __generator, __rest, __spreadArray } from "tslib";
import { fetch } from '../../lib/fetch';
import { onPageChange } from '../../lib/on-page-change';
import { RateLimitError } from './ratelimit-error';
import { Context } from '../../core/context';
import { computeBackoff, createHeaders, getStatusBehavior, parseRetryAfter, resolveHttpConfig, } from './shared-dispatcher';
var MAX_PAYLOAD_SIZE = 500;
var MAX_KEEPALIVE_SIZE = 64;
function kilobytes(buffer) {
    var size = encodeURI(JSON.stringify(buffer)).split(/%..|./).length - 1;
    return size / 1024;
}
/**
 * Checks if the payload is over or close to
 * the maximum payload size allowed by tracking
 * API.
 */
function approachingTrackingAPILimit(buffer) {
    return kilobytes(buffer) >= MAX_PAYLOAD_SIZE - 50;
}
/**
 * Checks if payload is over or approaching the limit for keepalive
 * requests. If keepalive is enabled we want to avoid
 * going over this to prevent data loss.
 */
function passedKeepaliveLimit(buffer) {
    return kilobytes(buffer) >= MAX_KEEPALIVE_SIZE - 10;
}
function chunks(batch) {
    var result = [];
    var index = 0;
    batch.forEach(function (item) {
        var size = kilobytes(result[index]);
        if (size >= 64) {
            index++;
        }
        if (result[index]) {
            result[index].push(item);
        }
        else {
            result[index] = [item];
        }
    });
    return result;
}
function buildBatch(buffer) {
    var batch = [];
    for (var i = 0; i < buffer.length; i++) {
        var event_1 = buffer[i];
        var candidate = __spreadArray(__spreadArray([], batch, true), [event_1], false);
        if (batch.length > 0 && approachingTrackingAPILimit(candidate)) {
            return { batch: batch, remaining: buffer.slice(i) };
        }
        batch.push(event_1);
    }
    return { batch: batch, remaining: [] };
}
export default function batch(apiHost, config, httpConfig, protocol) {
    var _a, _b;
    if (protocol === void 0) { protocol = 'https'; }
    var buffer = [];
    var pageUnloaded = false;
    var limit = (_a = config === null || config === void 0 ? void 0 : config.size) !== null && _a !== void 0 ? _a : 10;
    var timeout = (_b = config === null || config === void 0 ? void 0 : config.timeout) !== null && _b !== void 0 ? _b : 5000;
    var resolved = httpConfig !== null && httpConfig !== void 0 ? httpConfig : resolveHttpConfig();
    var rateLimitTimeout = 0;
    var requestCount = 0; // Tracks actual network requests for X-Retry-Count header
    var isRetrying = false;
    var isFlushing = false;
    var retryAfterRetries = 0;
    var totalBackoffTime = 0;
    var totalRateLimitTime = 0;
    function sendBatch(batch, retryCount) {
        var _a;
        if (batch.length === 0) {
            return;
        }
        var writeKey = (_a = batch[0]) === null || _a === void 0 ? void 0 : _a.writeKey;
        // Remove sentAt from every event as batching only needs a single timestamp
        var updatedBatch = batch.map(function (event) {
            var _a = event, sentAt = _a.sentAt, newEvent = __rest(_a, ["sentAt"]);
            return newEvent;
        });
        var headers = createHeaders(config === null || config === void 0 ? void 0 : config.headers);
        if (retryCount > 0) {
            headers['X-Retry-Count'] = String(retryCount);
        }
        if (writeKey) {
            var authtoken = btoa(writeKey + ':');
            headers['Authorization'] = "Basic ".concat(authtoken);
        }
        var scheme = apiHost.startsWith('http://') || apiHost.startsWith('https://')
            ? ''
            : "".concat(protocol, "://");
        return fetch("".concat(scheme).concat(apiHost, "/b"), {
            credentials: config === null || config === void 0 ? void 0 : config.credentials,
            keepalive: (config === null || config === void 0 ? void 0 : config.keepalive) || pageUnloaded,
            headers: headers,
            method: 'post',
            body: JSON.stringify({
                writeKey: writeKey,
                batch: updatedBatch,
                sentAt: new Date().toISOString(),
            }),
            // @ts-ignore - not in the ts lib yet
            priority: config === null || config === void 0 ? void 0 : config.priority,
        }).then(function (res) {
            var status = res.status;
            // Treat <400 as success (2xx/3xx)
            if (status < 400) {
                return;
            }
            // Determine retry/drop behavior from config (checks statusCodeOverrides first).
            var behavior = getStatusBehavior(status, resolved.backoffConfig);
            // Honor Retry-After for rate limiting, unless the status is explicitly
            // overridden to 'drop' via statusCodeOverrides.
            if (behavior !== 'drop') {
                var retryAfter = parseRetryAfter(res, resolved.rateLimitConfig);
                if (retryAfter) {
                    throw new RateLimitError("Rate limit exceeded: ".concat(status), retryAfter.retryAfterMs, retryAfter.fromHeader);
                }
            }
            // Retry via backoff when the status is retryable.
            if (behavior === 'retry') {
                throw new Error("Retryable error: ".concat(status));
            }
            // Non-retryable: silently drop
        });
    }
    function dropAndContinue() {
        if (buffer.length > 0) {
            scheduleFlush(1);
        }
    }
    function flush(attempt) {
        var _a;
        if (attempt === void 0) { attempt = 1; }
        return __awaiter(this, void 0, void 0, function () {
            var _b, batch_1, remaining, currentRetryCount;
            return __generator(this, function (_c) {
                if (!isRetrying) {
                    requestCount = 0;
                    retryAfterRetries = 0;
                    totalBackoffTime = 0;
                    totalRateLimitTime = 0;
                }
                isRetrying = false;
                isFlushing = true;
                if (buffer.length) {
                    _b = buildBatch(buffer), batch_1 = _b.batch, remaining = _b.remaining;
                    if (batch_1.length === 0) {
                        isFlushing = false;
                        return [2 /*return*/];
                    }
                    buffer = remaining;
                    currentRetryCount = requestCount;
                    requestCount += 1;
                    return [2 /*return*/, (_a = sendBatch(batch_1, currentRetryCount)) === null || _a === void 0 ? void 0 : _a.then(function (result) {
                            // If buildBatch left events due to payload size limits, schedule another flush
                            if (buffer.length > 0) {
                                scheduleFlush(1);
                            }
                            return result;
                        }).catch(function (error) {
                            var _a;
                            var ctx = Context.system();
                            ctx.log('error', 'Error sending batch', error);
                            var maxRetries = (_a = config === null || config === void 0 ? void 0 : config.maxRetries) !== null && _a !== void 0 ? _a : resolved.backoffConfig.maxRetryCount;
                            var isRateLimitError = error.name === 'RateLimitError';
                            var isRetryableWithoutCount = isRateLimitError && error.isRetryableWithoutCount;
                            var canRetry = isRetryableWithoutCount || attempt <= maxRetries;
                            if (!canRetry) {
                                return dropAndContinue();
                            }
                            // Rate-limit retries: enforce count cap and total duration cap
                            if (isRetryableWithoutCount) {
                                retryAfterRetries++;
                                if (retryAfterRetries > resolved.rateLimitConfig.maxRetryCount) {
                                    return dropAndContinue();
                                }
                                var delay = error.retryTimeout;
                                totalRateLimitTime += delay;
                                var maxRateLimitMs = resolved.rateLimitConfig.maxRateLimitDuration * 1000;
                                if (totalRateLimitTime > maxRateLimitMs) {
                                    return dropAndContinue();
                                }
                                rateLimitTimeout = delay;
                            }
                            // Backoff retries: compute delay, enforce total duration cap
                            var retryDelay;
                            if (!isRateLimitError) {
                                retryDelay = computeBackoff(attempt, resolved.backoffConfig);
                                totalBackoffTime += retryDelay;
                                var maxBackoffMs = resolved.backoffConfig.maxTotalBackoffDuration * 1000;
                                if (totalBackoffTime > maxBackoffMs) {
                                    return dropAndContinue();
                                }
                            }
                            buffer = __spreadArray(__spreadArray([], batch_1, true), buffer, true);
                            batch_1.forEach(function (event) {
                                if ('_metadata' in event) {
                                    var segmentEvent = event;
                                    segmentEvent._metadata = __assign(__assign({}, segmentEvent._metadata), { retryCount: attempt });
                                }
                            });
                            var nextAttempt = isRetryableWithoutCount ? attempt : attempt + 1;
                            isRetrying = true;
                            scheduleFlush(nextAttempt, retryDelay);
                        }).finally(function () {
                            isFlushing = false;
                        })];
                }
                else {
                    isFlushing = false;
                }
                return [2 /*return*/];
            });
        });
    }
    var schedule;
    function scheduleFlush(attempt, retryDelay) {
        if (attempt === void 0) { attempt = 1; }
        if (schedule) {
            return;
        }
        var delay = rateLimitTimeout || retryDelay || timeout;
        schedule = setTimeout(function () {
            schedule = undefined;
            flush(attempt).catch(console.error);
        }, delay);
        rateLimitTimeout = 0;
    }
    onPageChange(function (unloaded) {
        pageUnloaded = unloaded;
        if (pageUnloaded && buffer.length) {
            var reqs = chunks(buffer).map(function (b) { return sendBatch(b, 0); });
            Promise.all(reqs).catch(console.error);
        }
    });
    function dispatch(_url, body, _retryCountHeader) {
        return __awaiter(this, void 0, void 0, function () {
            var bufferOverflow;
            return __generator(this, function (_a) {
                buffer.push(body);
                // If a retry is pending (e.g., 429 rate-limit), don't bypass the scheduled retry.
                // A 429 blocks the entire flush iteration until the Retry-After period elapses.
                if (isRetrying) {
                    return [2 /*return*/];
                }
                bufferOverflow = buffer.length >= limit ||
                    approachingTrackingAPILimit(buffer) ||
                    ((config === null || config === void 0 ? void 0 : config.keepalive) && passedKeepaliveLimit(buffer));
                if (!bufferOverflow && !pageUnloaded) {
                    return [2 /*return*/, scheduleFlush()];
                }
                // If a flush is already in-flight, avoid concurrent flushes that would
                // corrupt shared mutable state (requestCount, totalBackoffTime, etc.).
                // Schedule instead so events are picked up after the current flush settles.
                return [2 /*return*/, isFlushing ? scheduleFlush() : flush()];
            });
        });
    }
    return {
        dispatch: dispatch,
    };
}
//# sourceMappingURL=batched-dispatcher.js.map